import { useState } from "react";
import { EmptyState, ErrorState, FreshnessBar, LoadingState, formatDateTime } from "../components/AdminPageStates";
import MetricCard from "../components/MetricCard";
import { useVerificationQueue } from "../hooks/useVerificationQueue";
import {
  VerificationQueueRow,
  VerificationQueueStatus,
  reviewVerificationRequest
} from "../services/verificationAdminService";

const statuses: Array<{ status: VerificationQueueStatus; label: string }> = [
  { status: "pending", label: "Pending" },
  { status: "approved", label: "Approved" },
  { status: "rejected", label: "Rejected" }
];

function label(value: string | null) {
  if (!value) {
    return "Unavailable";
  }

  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split("_")
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

function applicantMeta(row: VerificationQueueRow) {
  return [row.applicant.email, row.applicant.username ? `@${row.applicant.username}` : null, row.userId]
    .filter(Boolean)
    .join(" / ");
}

function evidenceText(row: VerificationQueueRow) {
  if (row.evidence.length === 0) {
    return "No evidence submitted";
  }

  return row.evidence.map((item) => `${item.label}: ${item.value}`).join(" / ");
}

function rowKey(row: VerificationQueueRow) {
  return row.requestId;
}

export default function Verification() {
  const [activeStatus, setActiveStatus] = useState<VerificationQueueStatus>("pending");
  const { data, error, isLoading, refresh } = useVerificationQueue(activeStatus);
  const [actionError, setActionError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [notesByRow, setNotesByRow] = useState<Record<string, string>>({});
  const [rejectionReasonByRow, setRejectionReasonByRow] = useState<Record<string, string>>({});
  const [reviewingRowKey, setReviewingRowKey] = useState("");

  function handleNotesChange(row: VerificationQueueRow, notes: string) {
    setNotesByRow((current) => ({ ...current, [rowKey(row)]: notes }));
  }

  function handleRejectionReasonChange(row: VerificationQueueRow, reason: string) {
    setRejectionReasonByRow((current) => ({ ...current, [rowKey(row)]: reason }));
  }

  async function handleReview(row: VerificationQueueRow, status: Exclude<VerificationQueueStatus, "pending">) {
    const key = rowKey(row);
    setReviewingRowKey(key);
    setActionError("");
    setActionMessage("");

    try {
      await reviewVerificationRequest({
        requestId: row.requestId,
        status,
        notes: notesByRow[key] ?? "",
        rejectionReason: rejectionReasonByRow[key] ?? ""
      });
      setActionMessage(status === "approved" ? "Verification approved." : "Verification rejected.");
      setNotesByRow((current) => ({ ...current, [key]: "" }));
      setRejectionReasonByRow((current) => ({ ...current, [key]: "" }));
      await refresh();
    } catch {
      setActionError("Unable to review verification request. Check admin permissions and required rejection reason.");
    } finally {
      setReviewingRowKey("");
    }
  }

  if (isLoading && !data) {
    return <LoadingState message="Loading verification queue." />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  if (!data) {
    return <EmptyState message="No verification queue data is available." />;
  }

  return (
    <>
      <FreshnessBar
        dataSource="fetchVerificationQueue Cloud Function"
        isLive={false}
        lastUpdated={data.fetchedAt}
        onRefresh={() => {
          void refresh();
        }}
      />
      {actionMessage ? <p className="success-inline">{actionMessage}</p> : null}
      {actionError ? <p className="form-error">{actionError}</p> : null}

      <div className="verification-tabs" role="tablist" aria-label="Verification status">
        {statuses.map((item) => (
          <button
            key={item.status}
            type="button"
            className={activeStatus === item.status ? "verification-tab active" : "verification-tab"}
            onClick={() => setActiveStatus(item.status)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="overview-grid">
        <MetricCard title={`${label(activeStatus)} requests`} value={String(data.requests.length)} note="Loaded from the production verification workflow." />
        <MetricCard title="Review source" value="Cloud Functions" note="Approve and reject actions call reviewVerificationRequest." />
      </div>

      <section className="detail-section">
        <h2>{label(activeStatus)} Verification Queue</h2>
        {data.requests.length > 0 ? (
          <div className="table-shell">
            <table className="admin-table verification-table">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Role</th>
                  <th>Evidence</th>
                  <th>Submitted</th>
                  <th>Review</th>
                </tr>
              </thead>
              <tbody>
                {data.requests.map((row) => {
                  const key = rowKey(row);
                  const isReviewing = reviewingRowKey === key;
                  const rejectionReason = rejectionReasonByRow[key] ?? row.rejectionReason ?? "";

                  return (
                    <tr key={key}>
                      <td>
                        <strong>{row.applicant.displayName}</strong>
                        <span>{applicantMeta(row)}</span>
                      </td>
                      <td>
                        {label(row.role)}
                        <span>{label(row.verificationType)}</span>
                      </td>
                      <td>{evidenceText(row)}</td>
                      <td>
                        {formatDateTime(row.submittedAt)}
                        {row.reviewedAt ? <span>Reviewed {formatDateTime(row.reviewedAt)}</span> : null}
                        {row.reviewedByEmail ?? row.reviewedBy ? <span>{row.reviewedByEmail ?? row.reviewedBy}</span> : null}
                      </td>
                      <td>
                        <div className="verification-actions">
                          <textarea
                            aria-label={`Review notes for ${row.applicant.displayName}`}
                            placeholder="Notes"
                            value={notesByRow[key] ?? row.notes ?? ""}
                            onChange={(event) => handleNotesChange(row, event.target.value)}
                          />
                          <input
                            aria-label={`Rejection reason for ${row.applicant.displayName}`}
                            placeholder="Rejection reason"
                            value={rejectionReason}
                            onChange={(event) => handleRejectionReasonChange(row, event.target.value)}
                          />
                          <div className="verification-action-buttons">
                            <button
                              type="button"
                              className="secondary-button"
                              disabled={isReviewing}
                              onClick={() => void handleReview(row, "approved")}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="secondary-button danger-button"
                              disabled={isReviewing || rejectionReason.trim().length === 0}
                              onClick={() => void handleReview(row, "rejected")}
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-inline">No {activeStatus} verification requests were found.</p>
        )}
      </section>
    </>
  );
}
